(() => {
  const SELECTOR =
    "[data-horizontal-wheel-scroll], .js-horizontal-wheel-scroll";
  const MOBILE_ONLY_SELECTOR = ".mobile-horizontal-wheel";
  const MOBILE_BREAKPOINT = 750;
  const DRAG_THRESHOLD = 8;
  const MOBILE_SWIPER_SLIDES_PER_VIEW = 1.15;

  const getRootNode = (root) => {
    if (!root) return document;
    if (
      root.nodeType === Node.DOCUMENT_NODE ||
      root.nodeType === Node.DOCUMENT_FRAGMENT_NODE
    )
      return root;
    if (root instanceof Element) return root;
    return document;
  };

  const syncMobileOnlyClasses = (root = document) => {
    const scope = getRootNode(root);
    const mobileOnlyElements = scope.querySelectorAll(MOBILE_ONLY_SELECTOR);
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    for (const element of mobileOnlyElements) {
      element.classList.toggle("js-horizontal-wheel-scroll", isMobile);

      if (!isMobile) {
        element.classList.remove("is-horizontal-dragging", "has-many-items");
      }
    }
  };

  const initHorizontalWheelScroll = (root = document) => {
    syncMobileOnlyClasses(root);

    const scope = getRootNode(root);
    const elements = scope.querySelectorAll(SELECTOR);
    const isMobileViewport = window.innerWidth < MOBILE_BREAKPOINT;

    const updateButtonsState = (container, prevButton, nextButton) => {
      if (!prevButton && !nextButton) return;

      const hasOverflow = container.scrollWidth > container.clientWidth;
      const maxScrollLeft = Math.max(
        container.scrollWidth - container.clientWidth,
        0,
      );
      const scrollLeft = container.scrollLeft;
      const canScrollLeft = hasOverflow && scrollLeft > 0;
      const canScrollRight = hasOverflow && scrollLeft < maxScrollLeft - 1;

      if (prevButton) {
        const disabled = !canScrollLeft;
        prevButton.disabled = disabled;
      }

      if (nextButton) {
        const disabled = !canScrollRight;
        nextButton.disabled = disabled;
      }
    };

    const getButtonScrollStep = (container) => {
      const children = Array.from(container.children).filter(
        (child) => child instanceof Element,
      );
      const fallbackAmount = container.clientWidth * 0.8 || 200;
      const firstChild = children[0];

      if (!firstChild) return fallbackAmount;

      let slotWidth = firstChild.getBoundingClientRect().width;

      if (children.length > 1) {
        const secondChild = children[1];
        const firstLeft = firstChild.getBoundingClientRect().left;
        const secondLeft = secondChild.getBoundingClientRect().left;
        const measuredSlotWidth = Math.abs(secondLeft - firstLeft);

        if (Number.isFinite(measuredSlotWidth) && measuredSlotWidth > 0) {
          slotWidth = measuredSlotWidth;
        }
      }

      const step = slotWidth;
      return Number.isFinite(step) && step > 0 ? step : fallbackAmount;
    };

    const getSwiperHost = (container) => {
      if (
        !(container instanceof Element) ||
        !container.classList.contains("swiper-wrapper")
      ) {
        return null;
      }

      const host = container.closest(".swiper");
      return host instanceof Element ? host : null;
    };

    const isSwiperReadyContainer = (container) => {
      if (typeof Swiper === "undefined") return false;
      if (!(container instanceof Element)) return false;
      if (!container.classList.contains("swiper-wrapper")) return false;

      const host = getSwiperHost(container);
      if (!host) return false;

      return Array.from(container.children).some(
        (child) =>
          child instanceof Element && child.classList.contains("swiper-slide"),
      );
    };

    const updateSwiperButtonsState = (swiper, prevButton, nextButton) => {
      if (!prevButton && !nextButton) return;

      const hasOverflow =
        !swiper.isLocked && swiper.slides.length > swiper.params.slidesPerView;
      const canScrollLeft = hasOverflow && !swiper.isBeginning;
      const canScrollRight = hasOverflow && !swiper.isEnd;

      if (prevButton) {
        prevButton.disabled = !canScrollLeft;
      }

      if (nextButton) {
        nextButton.disabled = !canScrollRight;
      }
    };

    const destroyMobileSwiper = (container, prevButton, nextButton) => {
      const host = getSwiperHost(container);
      const swiper = host && host.__mobileHorizontalSwiper;

      if (!swiper) return;

      swiper.destroy(true, true);
      host.__mobileHorizontalSwiper = null;

      if (prevButton || nextButton) {
        updateButtonsState(container, prevButton, nextButton);
      }
    };

    const initMobileSwiper = (container, prevButton, nextButton) => {
      if (!isMobileViewport || !isSwiperReadyContainer(container)) {
        destroyMobileSwiper(container, prevButton, nextButton);
        return null;
      }

      const host = getSwiperHost(container);
      if (!host) return null;

      if (
        host.__mobileHorizontalSwiper &&
        !host.__mobileHorizontalSwiper.destroyed
      ) {
        updateSwiperButtonsState(
          host.__mobileHorizontalSwiper,
          prevButton,
          nextButton,
        );
        return host.__mobileHorizontalSwiper;
      }

      const swiper = new Swiper(host, {
        slidesPerView: MOBILE_SWIPER_SLIDES_PER_VIEW,
        spaceBetween: 15,
        watchSlidesProgress: true,
        centeredSlides: true,
        breakpoints: {
          480: {
            slidesPerView: host.dataset.tabletView
              ? parseFloat(host.dataset.tabletView)
              : MOBILE_SWIPER_SLIDES_PER_VIEW,
          },
        },
        on: {
          init(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
          slideChange(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
          resize(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
          reachBeginning(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
          reachEnd(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
          fromEdge(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
          lock(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
          unlock(instance) {
            updateSwiperButtonsState(instance, prevButton, nextButton);
          },
        },
      });

      host.__mobileHorizontalSwiper = swiper;
      return swiper;
    };

    for (const element of elements) {
      const parent = element.parentElement;
      const prevButton = parent
        ? parent.querySelector("[data-horizontal-wheel-prev]")
        : null;
      const nextButton = parent
        ? parent.querySelector("[data-horizontal-wheel-next]")
        : null;
      const activeSwiper = initMobileSwiper(element, prevButton, nextButton);

      if (element.children.length > 4) {
        element.classList.add("has-many-items");
      } else {
        element.classList.remove("has-many-items");
      }

      // Avoid attaching multiple listeners to the same element
      if (element.__horizontalWheelScrollInitialized) {
        if (!activeSwiper && (prevButton || nextButton)) {
          updateButtonsState(element, prevButton, nextButton);
        }
        continue;
      }
      element.__horizontalWheelScrollInitialized = true;

      const dragState = {
        pointerId: null,
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        isPointerDown: false,
        isDragging: false,
        suppressClick: false,
      };

      const hasHorizontalOverflow = () =>
        element.scrollWidth > element.clientWidth;

      const hasActiveSwiper = () => {
        const host = getSwiperHost(element);
        return Boolean(
          host &&
          host.__mobileHorizontalSwiper &&
          !host.__mobileHorizontalSwiper.destroyed,
        );
      };

      const resetDragState = () => {
        dragState.pointerId = null;
        dragState.isPointerDown = false;
        dragState.isDragging = false;
        element.classList.remove("is-horizontal-dragging");
      };

      const shouldIgnorePointerDown = (event) => {
        if (hasActiveSwiper()) return true;
        if (!hasHorizontalOverflow()) return true;
        if (event.pointerType === "mouse" && event.button !== 0) return true;

        const interactiveTarget = event.target.closest(
          "input, select, textarea, label, summary, [data-no-drag-scroll]",
        );

        return Boolean(interactiveTarget);
      };

      element.addEventListener("pointerdown", (event) => {
        if (shouldIgnorePointerDown(event)) return;

        dragState.pointerId = event.pointerId;
        dragState.startX = event.clientX;
        dragState.startY = event.clientY;
        dragState.startScrollLeft = element.scrollLeft;
        dragState.isPointerDown = true;
        dragState.isDragging = false;
        // Pointer capture is deferred until a drag is confirmed in pointermove.
        // Calling it eagerly on pointerdown causes the browser to redirect the
        // subsequent click to the capturing element instead of the child that was
        // actually tapped, which breaks click-to-flip cards and similar interactions.
      });

      element.addEventListener("pointermove", (event) => {
        if (hasActiveSwiper()) return;
        if (!dragState.isPointerDown || dragState.pointerId !== event.pointerId)
          return;

        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;

        if (!dragState.isDragging) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            resetDragState();
            return;
          }

          dragState.isDragging = true;
          dragState.suppressClick = true;
          element.classList.add("is-horizontal-dragging");

          // Only capture the pointer once we know it's a real drag. This keeps
          // simple taps targeting the child element (so their click handlers fire)
          // while still letting drags continue outside the container bounds.
          if (typeof element.setPointerCapture === "function") {
            element.setPointerCapture(event.pointerId);
          }
        }

        event.preventDefault();
        element.scrollLeft = dragState.startScrollLeft - deltaX;
      });

      const scrollByAmount = (direction) => {
        const amount = getButtonScrollStep(element);
        element.scrollBy({
          left: direction * amount,
          behavior: "smooth",
        });
      };

      const endDrag = (event) => {
        if (hasActiveSwiper()) {
          resetDragState();
          return;
        }
        if (dragState.pointerId !== event.pointerId) return;

        if (
          typeof element.releasePointerCapture === "function" &&
          element.hasPointerCapture(event.pointerId)
        ) {
          element.releasePointerCapture(event.pointerId);
        }

        resetDragState();
      };

      element.addEventListener("pointerup", endDrag);
      element.addEventListener("pointercancel", endDrag);
      element.addEventListener("pointerleave", (event) => {
        if (event.pointerType !== "mouse" || !dragState.isDragging) return;
        endDrag(event);
      });

      element.addEventListener(
        "click",
        (event) => {
          if (hasActiveSwiper()) return;
          if (!dragState.suppressClick) return;

          dragState.suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true,
      );

      element.addEventListener("dragstart", (event) => {
        if (event.target instanceof Element && event.target.closest("img")) {
          event.preventDefault();
        }
      });

      if (prevButton || nextButton) {
        element.addEventListener("scroll", () => {
          if (hasActiveSwiper()) return;
          updateButtonsState(element, prevButton, nextButton);
        });

        if (prevButton) {
          prevButton.addEventListener("click", () => {
            const host = getSwiperHost(element);
            const swiper = host && host.__mobileHorizontalSwiper;
            if (swiper && !swiper.destroyed) {
              swiper.slidePrev();
              return;
            }
            if (prevButton.disabled) return;
            scrollByAmount(-1);
          });
        }

        if (nextButton) {
          nextButton.addEventListener("click", () => {
            const host = getSwiperHost(element);
            const swiper = host && host.__mobileHorizontalSwiper;
            if (swiper && !swiper.destroyed) {
              swiper.slideNext();
              return;
            }
            if (nextButton.disabled) return;
            scrollByAmount(1);
          });
        }

        updateButtonsState(element, prevButton, nextButton);
      }
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initHorizontalWheelScroll();
    });
  } else {
    initHorizontalWheelScroll();
  }

  let resizeFrame = null;
  window.addEventListener("resize", () => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);

    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      initHorizontalWheelScroll();
    });
  });

  document.addEventListener("shopify:section:load", (event) => {
    initHorizontalWheelScroll(event.target);
  });
})();
